import WarningIcon from "@mui/icons-material/Warning";
import { Box, CircularProgress, type SxProps, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type Dispatch, type FC, Fragment, type Ref, type SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import EmployeeStatusControl from "../../../../components/team/EmployeeStatusControl";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { CopyButton } from "../../../../components/UIToolkit/molecules/CopyButton";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useIsMobile } from "../../../../hooks/useMobile";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { STATUS } from "../../../../theme/tokens";
import { formatIsoDateTime } from "../../../../utils/dateUtils";
import { getNurseEffectiveStatus } from "../../../../utils/formatters";
import { EffectiveEmployeeStatus, type Nurse, type NurseWithExternalData, ProfileMode } from "../../../../utils/models";
import { ImagePicker } from "./ImagePicker";
import { NurseSwitcher } from "./NurseSwitcher";
import { useGeneralProfileDetailsState } from "./useGeneralProfileDetailsState";

const avatarSizeDesktop = 90;
const avatarSizeMobile = 50;
const primaryFontSizeDesktop = 22;
const secondaryFontSizeDesktop = 16;
const primaryFontSizeMobile = 14;
const secondaryFontSizeMobile = 12;
// Functional / Categorical: active/inactive employment indicator stays
// stable across themes (red = inactive, green = active).
const activeColor = "#009A0FCC";
const inactiveColor = "#E71C00CC";
const marginTop = 2;

/** The info-page status text reuses the chip's labels; color follows the same status semantics. */
const STATUS_LABEL_KEY: Record<EffectiveEmployeeStatus, string> = {
	[EffectiveEmployeeStatus.Active]: "employee.status.chip.active",
	[EffectiveEmployeeStatus.Inactive]: "employee.status.chip.inactive",
	[EffectiveEmployeeStatus.OnLeave]: "employee.status.chip.on_leave",
	[EffectiveEmployeeStatus.Suspended]: "employee.status.chip.suspended",
};
const statusTextColor = (status: EffectiveEmployeeStatus): string => {
	switch (status) {
		case EffectiveEmployeeStatus.OnLeave:
			return STATUS.WARNING.main;
		case EffectiveEmployeeStatus.Suspended:
			return STATUS.ERROR.main;
		case EffectiveEmployeeStatus.Inactive:
			return inactiveColor;
		default:
			return activeColor;
	}
};
const marginLeft = 4;

type GeneralProfileDetailsProps = {
	imageURL?: string;
	nurse?: NurseWithExternalData;
	profileMode: ProfileMode;
	isEditable: boolean;
	setNurseData: Dispatch<SetStateAction<Nurse>>;
	departmentId: string;
	refetchProfileRequested: () => void;
	createUserWhenCreatingNurse: boolean;
	setCreateUserWhenCreatingNurse: Dispatch<SetStateAction<boolean>>;
	setDisableSaveDueToAnIssueCreatingUser: Dispatch<SetStateAction<boolean>>;
	handleImageChanged: (file: File) => void;
	/** The view-mode name row — the profile page watches it to know when to repeat the name in its top bar. */
	nameRowRef: Ref<HTMLElement>;
};

const overflowTextSx = {
	overflow: "clip",
	textOverflow: "ellipsis",
	textWrap: "nowrap",
	maxWidth: {
		xs: "95%",
		md: "fit-content",
	},
} as const satisfies SxProps;

const nameSx = {
	fontSize: {
		xs: primaryFontSizeMobile,
		md: primaryFontSizeDesktop,
	},
	fontWeight: 700,
	lineHeight: "1.2",
	color: "primary.main",
	...overflowTextSx,
} as const satisfies SxProps;

/** The switcher pill's own padding would indent the name past the lines below it — pull it back flush. */
const switchableNameSx = [nameSx, { ml: -1 }] as const satisfies SxProps;

const viewModeDetailsSx = {
	width: {
		xs: "100%",
		md: `calc(100% - ${avatarSizeDesktop + 10}px)`,
	},
	ml: marginTop,
} as const satisfies SxProps;

const gridGap = {
	xs: 1,
	md: 2,
} as const;

export const GeneralProfileDetails: FC<GeneralProfileDetailsProps> = ({
	imageURL,
	profileMode,
	nurse,
	setNurseData,
	isEditable,
	departmentId,
	refetchProfileRequested,
	createUserWhenCreatingNurse,
	setCreateUserWhenCreatingNurse,
	setDisableSaveDueToAnIssueCreatingUser,
	handleImageChanged,
	nameRowRef,
}) => {
	const { isMobile } = useIsMobile();
	const effectiveStatus = nurse ? getNurseEffectiveStatus(nurse, departmentId) : EffectiveEmployeeStatus.Inactive;
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const formatPersonName = useFormatPersonName();
	const {
		partialNameValidation,
		firstName,
		lastName,
		onFirstNameChange,
		onLastNameChange,
		phoneNumber,
		phoneValidation,
		onPhoneChange,
		emailAddress,
		emailValidation,
		onEmailChange,
		handleImageEditClick,
		createUserSendResetButton,
		isThereAnIssueCreatingUser,
		nurseNameExistsWarning,
		isCheckingNurseName,
	} = useGeneralProfileDetailsState({
		nurse,
		setNurseData,
		departmentId,
		refetchProfileRequested,
		createUserWhenCreatingNurse,
		setCreateUserWhenCreatingNurse,
		profileMode,
	});

	useEffect(() => {
		setDisableSaveDueToAnIssueCreatingUser(isThereAnIssueCreatingUser);
	}, [isThereAnIssueCreatingUser, setDisableSaveDueToAnIssueCreatingUser]);

	const firstHrisSyncedAt = useMemo(() => {
		const syncDates = (nurse?.externalEmployees ?? [])
			.filter((employee) => employee.department_id === departmentId)
			.map((employee) => employee.created_at)
			.filter((createdAt): createdAt is string => Boolean(createdAt));
		if (!syncDates.length) return;

		return syncDates.reduce((earliest, current) => (DateTime.fromISO(current) < DateTime.fromISO(earliest) ? current : earliest));
	}, [nurse?.externalEmployees, departmentId]);

	return (
		<Box
			data-testid="profile-general-section"
			sx={{
				display: "flex",
				justifyContent: "start",
				width: "100%",
				height: "100%",

				alignItems: {
					xs: "start",
					md: "center",
				},
			}}
		>
			{!isMobile && (
				<ImagePicker
					isEditable={isEditable && profileMode !== ProfileMode.NEW}
					onFileChange={handleImageChanged}
					onClicked={handleImageEditClick}
					imageURL={imageURL}
					avatarSizeDesktop={avatarSizeDesktop}
					avatarSizeMobile={avatarSizeMobile}
					clickEventName={"profile"}
					clickEventSourceId={nurse?.id}
				/>
			)}
			{isEditable ? (
				<Box
					sx={{
						display: {
							xs: "flex",
							md: "grid",
						},

						flexWrap: "wrap",
						gridTemplateColumns: "repeat(3, 1fr)",
						gridTemplateRows: "repeat(2, auto)",
						gap: gridGap,

						width: {
							xs: "100%",
							md: `calc(100% - ${avatarSizeDesktop + 10}px)`,
						},

						ml: {
							xs: 0,
							md: marginLeft,
						},
					}}
				>
					<SystemValidationTextField
						data-testid="profile-first-name-input"
						formControlSx={{
							width: {
								xs: `calc(50% - ${gridGap.xs} * 4px)`,
								md: "100%",
							},
						}}
						required
						label={"First Name"}
						value={firstName}
						onChange={onFirstNameChange}
						validation={partialNameValidation}
						reserveHeightForHelperText={!isMobile}
					/>
					<SystemValidationTextField
						data-testid="profile-last-name-input"
						formControlSx={{
							width: {
								xs: `calc(50% - ${gridGap.xs} * 4px)`,
								md: "100%",
							},
						}}
						required
						label={"Last Name"}
						value={lastName}
						onChange={onLastNameChange}
						validation={partialNameValidation}
						reserveHeightForHelperText={!isMobile}
					/>
					{!isMobile && (
						<Box
							sx={{
								alignItems: "start",
								gap: "8px",
								display: "grid",
								gridTemplateColumns: `repeat(${createUserSendResetButton ? 2 : 1}, 1fr)`,
							}}
						>
							{/*todo enable nickname component*/}
							{/*<SystemValidationTextField*/}
							{/*	formControlSx={{ width: "250px" }}*/}
							{/*	label={t("nickname")}*/}
							{/*	value={fullName}*/}
							{/*	onChange={onNameChange}*/}
							{/*	validation={fullNameValidation}*/}
							{/*	disabled*/}
							{/*/>*/}
							{createUserSendResetButton && (
								<Box
									sx={{
										pt: 1,
									}}
								>
									{createUserSendResetButton}
								</Box>
							)}
						</Box>
					)}

					<SystemValidationTextField
						data-testid="profile-phone-input"
						formControlSx={{
							width: "100%",
						}}
						label={t("phone.number")}
						value={phoneNumber}
						onChange={onPhoneChange}
						validation={phoneValidation}
						inputType={"tel"}
						reserveHeightForHelperText={!isMobile}
					/>

					<SystemValidationTextField
						data-testid="profile-email-input"
						formControlSx={{
							width: "100%",
						}}
						label={t("email")}
						value={emailAddress}
						onChange={onEmailChange}
						validation={emailValidation}
						inputType={"email"}
						reserveHeightForHelperText={!isMobile}
					/>
					{isMobile && createUserSendResetButton && (
						<Box
							sx={{
								pt: 1,
							}}
						>
							{createUserSendResetButton}
						</Box>
					)}
					<Box
						style={{
							opacity: nurseNameExistsWarning || isCheckingNurseName ? 1 : 0,
							transition: "opacity 0.150s ease-in-out",
						}}
						sx={{
							pt: 1,
							display: "flex",
							gap: 1,
						}}
					>
						{isCheckingNurseName ? (
							<CircularProgress size={14} sx={{ color: STATUS.WARNING.main }} />
						) : (
							<WarningIcon style={{ fontSize: "14px", color: STATUS.WARNING.main }} />
						)}
						<Typography
							sx={{
								fontSize: "12px",
								fontWeight: 700,
								color: STATUS.WARNING.main,
							}}
						>
							{nurseNameExistsWarning}
						</Typography>
					</Box>
					{nurse && profileMode !== ProfileMode.NEW && (
						<Box sx={{ mt: marginTop }}>
							<EmployeeStatusControl nurse={nurse} departmentId={departmentId} onStatusChanged={refetchProfileRequested} />
						</Box>
					)}
				</Box>
			) : (
				<Fragment>
					<Box
						sx={[
							{
								display: "flex",
								flexDirection: "column",
							},
							...(Array.isArray(viewModeDetailsSx) ? viewModeDetailsSx : [viewModeDetailsSx]),
						]}
					>
						{nurse?.id ? (
							<Box ref={nameRowRef} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, maxWidth: "100%" }}>
								{/*
								 * The name is the switcher (APP-535) — jumping to another profile from here beats
								 * a round trip through Team. Only for a saved employee: an unsaved one has no roster
								 * entry, and switching away mid-creation would drop the form.
								 */}
								<NurseSwitcher nurse={nurse} sx={switchableNameSx} />
								{/* The name is what gets retyped into payroll/HRIS/agency tools all day — hand it over in one click. */}
								<CopyButton
									value={formatPersonName(nurse)}
									label={t("profile.copy.employee.name")}
									onCopied={() => track("profile_copy_employee_name", { nurse_id: nurse.id })}
									dataTestId="profile-copy-employee-name"
									sx={{ flexShrink: 0 }}
								/>
							</Box>
						) : (
							<Typography ref={nameRowRef} sx={nameSx}>
								{nurse && formatPersonName(nurse)}
							</Typography>
						)}

						<Typography
							sx={(theme) => ({
								mt: marginTop,
								fontSize: { xs: secondaryFontSizeMobile, md: secondaryFontSizeDesktop },
								fontWeight: 500,
								lineHeight: "1.2",
								color: theme.palette.primary.main,
								...overflowTextSx,
							})}
						>
							<Box component="span" sx={{ color: statusTextColor(effectiveStatus) }}>
								{t(STATUS_LABEL_KEY[effectiveStatus])}
							</Box>
							<Box component="span" sx={{ "&::before": { content: '"|"', pl: 1.5, pr: 2 } }}>
								{t("last.login")}: {nurse?.lastLoginTime ? nurse.lastLoginTime.toFormat("MMM d, yyyy HH:mm") : t("never")}
							</Box>
						</Typography>

						{firstHrisSyncedAt && (
							<Typography
								data-testid="profile-first-hris-sync"
								sx={(theme) => ({
									mt: marginTop,
									fontSize: { xs: secondaryFontSizeMobile, md: secondaryFontSizeDesktop },
									fontWeight: 500,
									lineHeight: "1.2",
									color: theme.palette.primary.main,
									...overflowTextSx,
								})}
							>
								{t("first.hris.sync")}: {formatIsoDateTime(firstHrisSyncedAt)}
							</Typography>
						)}

						<Typography
							sx={(theme) => ({
								mt: marginTop,

								fontSize: {
									xs: secondaryFontSizeMobile,
									md: secondaryFontSizeDesktop,
								},

								fontWeight: 700,
								lineHeight: "1.2",
								color: theme.palette.primary.main,
								...overflowTextSx,
							})}
						>
							{[nurse?.phoneNumber, nurse?.email].filter(Boolean).join(" | ")}
						</Typography>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							alignItems: "start",
							justifyContent: "space-between",
							paddingBlock: "16px",
							position: "relative",
						}}
					>
						{createUserSendResetButton}
					</Box>
				</Fragment>
			)}
		</Box>
	);
};

export default GeneralProfileDetails;
